// ---------------------------------------------------------------------------
// useUnsavedChangesGuard
//
// Extracted verbatim-in-spirit from PlanogramDetails.tsx's
// hasUnsavedChanges/showUnsavedChangesDialog/pendingNavigationTarget state
// + shouldAllowNavigationOrShowConfirmSaveDialog +
// handleSwitchMenuOnClick/handleBackButtonClick + the beforeunload effect +
// handleUnsavedDialogHide/handleUnsavedChangesConfirm/handleUnsavedChangesDiscard.
//
// This is the bug-fix-critical piece from your original comments: the dirty
// flag is an EXPLICIT boolean set by every mutating action, never derived
// from a deep-equality check — that's the actual fix for "unsaved-changes
// dialog never appeared." This hook is where that explicit flag lives now,
// instead of being threaded through PlanogramDetails directly.
//
// Wiring contract:
//   - Call `markDirty()` from every mutation path (usePlanogramEditor's
//     onMutate option is the natural place to wire this in).
//   - Call `markClean()` after a successful save AND after a fresh
//     load/fetch (both "the in-memory state now matches what's
//     persisted").
//   - `guardNavigation(target)` returns true if navigation should proceed
//     immediately, false if it opened the confirm dialog instead (caller
//     must not navigate in that case).
//   - The two affordances that historically bypassed the guard — the
//     chevron back-button (event.preventDefault required for the `<a>` tag
//     case) and the "Planograms" breadcrumb text (used to call
//     navigate(...) directly) — both route through `guardNavigation` here,
//     so there's exactly one implementation of "is it safe to leave" for
//     every entry point to share.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';

export interface UseUnsavedChangesGuardOptions {
    /** Called once navigation is confirmed safe (no unsaved changes, or the person chose Discard) — perform the actual navigate(target) here. */
    onNavigate: (target: string) => void;
    /** Called when the person chooses "Save" in the confirm dialog — perform the actual save, returning a promise. Resolve target navigation happens after this resolves. */
    onSaveBeforeNavigate: () => Promise<void>;
}

export function useUnsavedChangesGuard({ onNavigate, onSaveBeforeNavigate }: UseUnsavedChangesGuardOptions) {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [pendingTarget, setPendingTarget] = useState<string | null>(null);

    const markDirty = useCallback(() => setHasUnsavedChanges(true), []);
    const markClean = useCallback(() => setHasUnsavedChanges(false), []);

    /** Returns true if navigation may proceed immediately; false if the confirm dialog was opened instead (caller must NOT navigate). */
    const guardNavigation = useCallback((target: string): boolean => {
        if (hasUnsavedChanges) {
            setPendingTarget(target);
            setShowDialog(true);
            return false;
        }
        return true;
    }, [hasUnsavedChanges]);

    /**
     * Wraps a click handler so it works both as a plain callback (only the
     * boolean return value matters) AND as a native anchor's onClick (where
     * preventDefault/stopPropagation are required to stop the browser from
     * following an href regardless of the returned boolean) — see the
     * original "chevron back-button bypassing the guard" bug.
     */
    const guardedClickHandler = useCallback((target: string) => (
        event?: { preventDefault?: () => void; stopPropagation?: () => void },
    ): boolean => {
        const canProceed = guardNavigation(target);
        if (!canProceed) {
            event?.preventDefault?.();
            event?.stopPropagation?.();
        }
        return canProceed;
    }, [guardNavigation]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    /** ConfirmDialog's onHide — fires on both X-close and after onConfirm resolves. Only clears dialog UI state, never navigates. */
    const handleDialogHide = useCallback(() => {
        setShowDialog(false);
        setPendingTarget(null);
    }, []);

    /**
     * "Save" button in the confirm dialog. Captures pendingTarget before
     * saving because ConfirmDialog's own onHide (which clears pendingTarget)
     * fires automatically after this resolves.
     */
    const confirmSaveAndNavigate = useCallback(async (fallbackTarget: string) => {
        const target = pendingTarget ?? fallbackTarget;
        await onSaveBeforeNavigate();
        onNavigate(target);
    }, [pendingTarget, onSaveBeforeNavigate, onNavigate]);

    /**
     * "Discard" button. ConfirmDialog's cancel button calls onCancel
     * directly (not onConfirm), so onHide does NOT fire automatically —
     * the dialog must be hidden explicitly here.
     */
    const discardAndNavigate = useCallback((fallbackTarget: string) => {
        const target = pendingTarget ?? fallbackTarget;
        setHasUnsavedChanges(false);
        handleDialogHide();
        onNavigate(target);
    }, [pendingTarget, handleDialogHide, onNavigate]);

    return {
        hasUnsavedChanges,
        showDialog,
        markDirty,
        markClean,
        guardNavigation,
        guardedClickHandler,
        handleDialogHide,
        confirmSaveAndNavigate,
        discardAndNavigate,
    };
}

export type UseUnsavedChangesGuardReturn = ReturnType<typeof useUnsavedChangesGuard>;